import { useCallback, useEffect, useState } from "react";
import logger from "logging/logger";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import useCompanySettingsActions from "modules/companySettings/hooks/useCompanySettingsActions";
import { getProvider } from "services/apiService/apis";
import { useI18n } from "i18n";
import { selectMyCompany } from "modules/company/redux/selectors";
import { selectCompanySettings } from "modules/companySettings/redux/selectors";
import { useSelector } from "react-redux";
import { ICompanySettings, IIdentityProvider } from "modules/company/types";
import {
  createProviderAsync,
  createSamlConfigAsync,
  updateProviderAsync,
  updateSamlConfigAsync,
} from "../SsoApiService";
import { ISsoStatus } from "../SsoConfiguration";
import { ISsoInfo, IConfigProvider } from "../types";

const useSso = () => {
  const { addSuccessNotification, addErrorNotification } =
    useNotificationActions();
  const { setCompanySettings } = useCompanySettingsActions();
  const { t } = useI18n(["common", "sso", "server"]);

  const company = useSelector(selectMyCompany);
  const companySettings: ICompanySettings = useSelector(selectCompanySettings);
  const SamlProvider = (
    companySettings?.identityProviders?.type === "saml2"
      ? companySettings.identityProviders
      : {}
  ) as IIdentityProvider;

  const [isNewlyCreated, setIsNewlyCreated] = useState(false);
  const [ssoStatus, setSsoStatus] = useState<ISsoStatus>(ISsoStatus.PENDING);
  const [isNew, setIsNew] = useState(!SamlProvider?.providerId);

  const [configInfo, setConfigInfo] = useState<ISsoInfo>({
    authenticationEndpoint: "",
    identityProviderEntityID: "",
    publicCertificate: "",
    providerId: SamlProvider?.providerId,
    displayName: SamlProvider?.name,
    serviceProviderEntityID: "",
    acsUrl: "",
    enabled: false,
  });
  const [isReady, setIsReady] = useState(!configInfo?.providerId);
  let errorMessage = t("sso:notification.saving.error");

  const createNewProvider = useCallback(
    async (data: ISsoInfo) => {
      let success = false;
      let companySettingsRes: APIResponse<{
        companySettings?: ICompanySettings;
      }> = {
        isSuccess: false,
      };

      try {
        // Create Identity Provider
        const samlProviderRes = await createProviderAsync(data);

        if (
          samlProviderRes.isSuccess &&
          samlProviderRes.provider &&
          data.displayName &&
          company?.id
        ) {
          // Add new Identity Provider in the Company settings
          companySettingsRes = await createSamlConfigAsync(company.id, data);

          success = !!companySettingsRes.isSuccess;
        } else {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          errorMessage = t(samlProviderRes.message || errorMessage);

          success = false;
          logger.error("[useSso][createNewProvider] conditional error", {
            data,
            samlProviderRes,
            company,
          });
        }
      } catch (error) {
        success = false;

        if (error instanceof Object) {
          logger.error("[useSso][createNewProvider] try/catch error", {
            error,
          });
        }

        errorMessage = t(error instanceof Error ? error.message : errorMessage);
      }

      if (success && companySettingsRes.isSuccess) {
        setCompanySettings(companySettingsRes.companySettings);
      }

      return success;
    },
    [company],
  );

  const updateExistingProvider = useCallback(
    async (data: ISsoInfo) => {
      let success = false;
      let companySettingsRes: APIResponse<{
        companySettings?: ICompanySettings;
      }> = {
        isSuccess: false,
      };

      try {
        // Update Identity Provider
        const samlProviderRes = await updateProviderAsync(data);

        if (
          samlProviderRes.isSuccess &&
          samlProviderRes.provider &&
          data.displayName &&
          company?.id
        ) {
          // Update Identity Provider in the Company settings
          companySettingsRes = await updateSamlConfigAsync(company.id, data);

          success = !!companySettingsRes.isSuccess;
        } else {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          errorMessage = t(samlProviderRes.message || errorMessage);

          success = false;
          logger.error("[useSso][updateExistingProvider] conditional error", {
            data,
            samlProviderRes,
            company,
          });
        }
      } catch (error) {
        success = false;

        if (error instanceof Object) {
          logger.error("[useSso][updateExistingProvider] try/catch error", {
            error,
          });
        }

        errorMessage = t(error instanceof Error ? error.message : errorMessage);
      }

      if (success && companySettingsRes.isSuccess) {
        setCompanySettings(companySettingsRes.companySettings);
      }

      return success;
    },
    [company],
  );

  const onSyncProvider = useCallback(
    async (partialData: Partial<ISsoInfo>) => {
      const data = { ...configInfo, ...partialData };

      let successMessage = "";

      if (isNew) {
        successMessage = t("sso:notification.saved.new");
      } else {
        successMessage = data.enabled
          ? t("sso:notification.saved.live")
          : t("sso:notification.saved.draft");
      }

      const success = isNew
        ? await createNewProvider(data)
        : await updateExistingProvider(data);

      if (success) {
        setConfigInfo(data);
        addSuccessNotification({
          message: successMessage,
          position: "tc",
          autoDismiss: isNew ? 12 : 5,
        });

        if (isNew) setIsNewlyCreated(true);
      } else {
        addErrorNotification({
          message: errorMessage,
          position: "tc",
          autoDismiss: 8,
        });
      }

      return success;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isNew, configInfo],
  );

  const fetchProvider = useCallback(
    async (providerId: string) => {
      let provider: IConfigProvider | undefined;

      try {
        const samlProviderRes = await getProvider(providerId);

        if (samlProviderRes?.isSuccess) {
          provider = samlProviderRes.provider;
        }
      } catch (error) {
        logger.error("[useSso][fetchProvider]", { error });
      }

      setIsReady(true);

      if (provider) {
        setConfigInfo({
          ...configInfo,
          displayName: provider.displayName ?? "",
          authenticationEndpoint: provider.ssoURL ?? "",
          identityProviderEntityID: provider.idpEntityId ?? "",
          publicCertificate: provider.x509Certificates?.length
            ? provider.x509Certificates[0]
            : "",
          acsUrl: provider.callbackURL,
          serviceProviderEntityID: provider.rpEntityId,
          enabled: provider.enabled,
          providerId: provider.providerId,
        });

        setSsoStatus(
          provider.enabled ? ISsoStatus.ACTIVE : ISsoStatus.INACTIVE,
        );
      } else {
        setIsNew(true);
        addErrorNotification({
          message: t("sso:identity.not.found"),
          position: "tc",
          autoDismiss: 8,
        });
        setConfigInfo({
          ...configInfo,
          displayName: "",
          providerId: undefined,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configInfo],
  );

  useEffect(() => {
    setSsoStatus(
      SamlProvider?.isEnabled ? ISsoStatus.ACTIVE : ISsoStatus.INACTIVE,
    );
  }, [SamlProvider?.isEnabled]);

  useEffect(() => {
    setIsNew(!SamlProvider?.providerId);
  }, [SamlProvider?.providerId]);

  return {
    isNew,
    isReady,
    isNewlyCreated,
    ssoStatus,
    configInfo,
    onSyncProvider,
    fetchProvider,
  };
};

export default useSso;
